// Browser read helpers for prize-fundraiser 1..N products (UI cap 8).
//
// Canonical writer/normalize lives in ../../../lib/prize-tiers.js (copied from
// shared/prize-tiers.js). This file is a TypeScript port of that lib's READ
// half — tiersFromSubmission and its helpers mirror the JS byte-for-byte in
// behavior, and prize-tiers.contract.test.ts pins the two together on shared
// fixtures. If you change the read rule here, change shared/prize-tiers.js
// (and its 24 guarded copies) first, then re-run the contract test.
//
// Read rule (post PR #379): a non-empty `submissions.tiers[]` is source of
// truth; each entry's product index is the STORED `index` / `product_index`
// (storedEntryIndex), with position+1 only as a fallback for sequential
// writers. `[{index:1},{index:3}]` is products 1 and 3 — never 1 and 2.
// Empty `[]` falls through to legacy T1/T2 so a botched write cannot blank a
// live fundraiser. Duplicate indexes stay readable — only WRITES refuse them,
// and the console client never persists tiers[] directly (server routes use
// the guarded JS copy's indexTiersForWrite).
//
// Catalog `prizeProducts.tiers` is a different field ([1, 2] eligibility) and
// is NOT read here.

export const MAX_PRIZE_TIERS = 8;

// Suffixes dual-written as tier{1|2}{Suffix} ↔ entry.{camelSuffix}.
// Keep identical to shared/prize-tiers.js — the contract test compares them.
export const LEGACY_TIER_SUFFIXES: readonly string[] = Object.freeze([
  'Product',
  'ProductWas',
  'ProductBackfilledAt',
  'StyleCode',
  'UseSameLogo',
  'DecorationSlots',
  'Decoration',
  'LogoPath',
  'LibraryLogoId',
  'LogoSource',
  'LogoSourcePath',
  'LogoSourceRef',
  'LogoSha256',
  // Identity PAIRS travel together (2026-09-14) — mirrors shared/prize-tiers.js.
  'LogoGeneration',
  'SourceFormat',
  'LogoSourceVerifiedAt',
  'BaseLogoPath',
  'BaseLogoGeneration',
  'BaseLogoSha256',
  'PreviousLogoPath',
  'TextAdded',
  'TextAddedAt',
  'FontUsed',
  'PreflightStatus',
  'PreflightDesignId',
  'PreflightDimensions',
  'PreflightAt',
  'PreflightCheckedAt',
  'PreflightArtName',
  'PreflightBoundingBox',
  'TrimmedLogoPath',
  'DesignIdAutoGenerated',
  'MockupPath',
  'MockupPaths',
  'MockupUrl',
  'MockupError',
  'ShopifyProductId',
  'ShopifyProductUrl',
  'PublicUrl',
  'ProductHandle',
  'VariantCount',
  'ImageCount',
  'Variants',
  'AddedToCollection',
  'CollectId',
  'CollectionError',
  'DecorationMetafieldsAt',
  'DecorationMetafieldsError',
]);

// A legacy tier EXISTS only when one of these has a value. Mockup leftovers
// (tier2MockupError, tier2MockupUrl) deliberately do not count.
export const EXISTENCE_SUFFIXES: readonly string[] = Object.freeze([
  'Product', 'ShopifyProductId', 'LogoPath', 'DecorationSlots',
]);

export type PrizeTier = {
  index: number;
  product?: string | null;
  decorationSlots?: unknown;
  logoPath?: string | null;
  shopifyProductId?: string | null;
  shopifyProductUrl?: string | null;
  mockupPath?: string | null;
  mockupUrl?: string | null;
  mockupPaths?: Record<string, string> | null;
  preflightStatus?: string | null;
  preflightDesignId?: string | number | null;
  preflightAt?: string | null;
  useSameLogo?: boolean | null;
  [key: string]: unknown;
};

type AnyDoc = Record<string, unknown>;

function camelKey(suffix: string): string {
  return suffix.charAt(0).toLowerCase() + suffix.slice(1);
}

function legacyFieldName(index: number, suffix: string): string {
  return `tier${index}${suffix}`;
}

function hasValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function isPlainObject(value: unknown): value is AnyDoc {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function cloneJson<T>(value: T): T {
  if (value === undefined) return value;
  return JSON.parse(JSON.stringify(value)) as T;
}

function normalizeSlots(slots: unknown, where: string): unknown {
  if (slots === undefined || slots === null) return slots;
  if (!Array.isArray(slots)) {
    throw new Error(`${where}: decorationSlots must be an array (got ${typeof slots})`);
  }
  return slots.map((slot) => {
    if (!isPlainObject(slot)) return slot;
    return { ...slot, kind: slot.kind || 'logo' };
  });
}

export function assertTierCap(tiers: unknown): asserts tiers is unknown[] {
  if (!Array.isArray(tiers)) {
    throw new Error(`assertTierCap: tiers must be an array (got ${tiers === null ? 'null' : typeof tiers})`);
  }
  if (tiers.length > MAX_PRIZE_TIERS) {
    throw new Error(
      `assertTierCap: ${tiers.length} products exceeds MAX_PRIZE_TIERS (${MAX_PRIZE_TIERS}). ` +
      'The UI stops at 8; refuse the write here rather than persisting a 9th card the forms cannot edit.',
    );
  }
}

export function prizeIndexes(count: number): number[] {
  const n = Math.max(0, Math.min(MAX_PRIZE_TIERS, Number(count) || 0));
  return Array.from({ length: n }, (_, i) => i + 1);
}

export function canAddPrizeProduct(count: number): boolean {
  return (Number(count) || 0) < MAX_PRIZE_TIERS;
}

/**
 * THE product-index resolver — the same rule as the guarded JS lib. Honor
 * stored `index` / `product_index`; sequential writers that omit both still
 * get position+1. Overwriting with position+1 turned `[{index:1},{index:3}]`
 * into products 1 and 2 — product 3 must stay 3.
 */
export function storedEntryIndex(raw: unknown, position: number): number {
  const r = raw as AnyDoc | null | undefined;
  const fromIndex = Number(r && r.index);
  if (Number.isInteger(fromIndex) && fromIndex >= 1) return fromIndex;
  const fromProduct = Number(r && r.product_index);
  if (Number.isInteger(fromProduct) && fromProduct >= 1) return fromProduct;
  return position + 1;
}

function normalizeStoredEntry(raw: unknown, position: number): PrizeTier {
  if (!isPlainObject(raw)) {
    throw new Error(`tiersFromSubmission: tiers[${position}] must be an object (got ${raw === null ? 'null' : typeof raw})`);
  }
  const entry = cloneJson(raw) as PrizeTier;
  entry.index = storedEntryIndex(raw, position);
  if (entry.decorationSlots !== undefined) {
    entry.decorationSlots = normalizeSlots(entry.decorationSlots, `tiers[${position}].decorationSlots`);
  }
  return entry;
}

function legacyTierExists(doc: AnyDoc, index: number): boolean {
  for (const suffix of EXISTENCE_SUFFIXES) {
    if (hasValue(doc[legacyFieldName(index, suffix)])) return true;
  }
  if (index === 1) {
    if (hasValue(doc.apparelPrize) || hasValue(doc.incomingPath)) return true;
  }
  return false;
}

function readLegacyTier(doc: AnyDoc, index: number): PrizeTier {
  const entry: PrizeTier = { index };
  for (const suffix of LEGACY_TIER_SUFFIXES) {
    const field = legacyFieldName(index, suffix);
    if (!Object.prototype.hasOwnProperty.call(doc, field)) continue;
    const key = camelKey(suffix);
    if (suffix === 'DecorationSlots') {
      entry[key] = normalizeSlots(doc[field], legacyFieldName(index, suffix));
    } else {
      entry[key] = cloneJson(doc[field]);
    }
  }
  if (index === 1) {
    if (!hasValue(entry.product) && hasValue(doc.apparelPrize)) {
      entry.product = doc.apparelPrize as string;
    }
    if (!hasValue(entry.logoPath) && hasValue(doc.incomingPath)) {
      entry.logoPath = doc.incomingPath as string;
    }
  }
  if (entry.decorationSlots !== undefined) {
    entry.decorationSlots = normalizeSlots(entry.decorationSlots, `tier${index}DecorationSlots`);
  }
  return entry;
}

function synthesizeFromLegacy(doc: AnyDoc): PrizeTier[] {
  const out: PrizeTier[] = [];
  for (let index = 1; index <= 2; index += 1) {
    if (!legacyTierExists(doc, index)) continue;
    out.push(readLegacyTier(doc, index));
  }
  return out;
}

/**
 * Normalized product list for a submission.
 *
 * Read rule: a non-empty `doc.tiers` array is source of truth (indexes from
 * storedEntryIndex, NOT array position); otherwise synthesize from legacy
 * T1/T2 (and T1 aliases apparelPrize / incomingPath). Empty `tiers: []` falls
 * through. Does not throw on N>8.
 */
export function tiersFromSubmission(doc: AnyDoc | null | undefined): PrizeTier[] {
  const data = doc || {};
  if (Array.isArray(data.tiers) && data.tiers.length > 0) {
    return data.tiers.map((raw, i) => normalizeStoredEntry(raw, i));
  }
  return synthesizeFromLegacy(data);
}

export type MintedShopifyProduct = {
  index: number;
  product?: string | null;
  shopifyProductId: string;
  mockupUrl?: string | null;
  mockupPath?: string | null;
  logoPath?: string | null;
};

/**
 * Minted Shopify product ids for Place Order. Prefers `tiers[].shopifyProductId`,
 * then `shopifyProductIds[]`, then legacy `tier{N}ShopifyProductId` — all keyed
 * by the entry's RESOLVED product index, so a gapped doc's product 3 never
 * reads the tier2 flats. Empty T2 (no id) is omitted — N=1 does not invent a
 * second card.
 */
export function mintedShopifyProducts(doc: AnyDoc | null | undefined): MintedShopifyProduct[] {
  const data = doc || {};
  const idsArray = Array.isArray(data.shopifyProductIds) ? data.shopifyProductIds as unknown[] : [];
  const fromTiers: MintedShopifyProduct[] = [];
  for (const t of tiersFromSubmission(data)) {
    const fromLegacy = data[`tier${t.index}ShopifyProductId`];
    const fromArray = idsArray[t.index - 1];
    const shopifyProductId = String(
      t.shopifyProductId || fromLegacy || fromArray || (t.index === 1 ? data.shopifyProductId : '') || '',
    ).trim();
    if (!shopifyProductId) continue;
    fromTiers.push({
      index: t.index,
      product: t.product,
      shopifyProductId,
      mockupUrl: (t.mockupUrl || data[`tier${t.index}MockupUrl`] || null) as string | null,
      mockupPath: (t.mockupPath || data[`tier${t.index}MockupPath`] || null) as string | null,
      logoPath: (t.logoPath || data[`tier${t.index}LogoPath`] || null) as string | null,
    });
  }
  if (fromTiers.length) return fromTiers;
  const legacyId = String(data.shopifyProductId || '').trim();
  if (legacyId) {
    return [{
      index: 1,
      product: (data.tier1Product || data.apparelPrize || null) as string | null,
      shopifyProductId: legacyId,
      mockupUrl: (data.tier1MockupUrl || null) as string | null,
      mockupPath: (data.tier1MockupPath || null) as string | null,
      logoPath: (data.tier1LogoPath || data.incomingPath || null) as string | null,
    }];
  }
  return [];
}

export function productOfTier(doc: AnyDoc | null | undefined, index: number): string | undefined {
  const found = tiersFromSubmission(doc).find((t) => t.index === index);
  if (found?.product) return String(found.product);
  const data = doc || {};
  if (index === 1) return (data.tier1Product || data.tier1_product || data.apparelPrize || undefined) as string | undefined;
  if (index === 2) return (data.tier2Product || data.tier2_product || undefined) as string | undefined;
  return undefined;
}
